/* ============================================================
   Wido Ops — app shell: nav, role switcher, theme, routing, tweaks.
   ============================================================ */
(function () {
  const { useState, useEffect } = React;
  const D = window.WidoData;
  const AV = window.WidoAvatar;
  const USE_API = !!window.WIDO_USE_API;

  // ---- URL routing (History API) ----
  // Each feature maps to a path so the address bar reflects the page and the
  // browser back/forward buttons work. The employee drill-in is /employee/:id.
  const ROUTE_PATHS = {
    dashboard: "/", timesheet: "/timesheet", requests: "/requests",
    approvals: "/approvals", summary: "/summary", records: "/records",
    override: "/override", missing: "/missing-clocks", activations: "/employees", teams: "/teams",
    devices: "/devices", settings: "/settings", access: "/access",
    holidays: "/holidays", calendar: "/calendar", meetings: "/meetings", policies: "/policies",
    hoursreport: "/hours-report", system: "/system",
  };
  const PATH_ROUTES = Object.fromEntries(Object.entries(ROUTE_PATHS).map(([k, v]) => [v, k]));
  // Backend notification deep-links use a few names that differ from our paths.
  const ROUTE_ALIASES = { "/accounts": "activations", "/activations": "activations", "/people": "activations" };
  function routeFromPath(pathname) {
    const m = pathname.match(/^\/employee\/(.+)$/);
    if (m) return { route: "employee", id: decodeURIComponent(m[1]) };
    return { route: PATH_ROUTES[pathname] || ROUTE_ALIASES[pathname] || "dashboard", id: null };
  }
  function pathForRoute(route, detailPerson) {
    if (route === "employee" && detailPerson) return "/employee/" + encodeURIComponent(detailPerson.id);
    return ROUTE_PATHS[route] || "/";
  }

  // ---- Error boundary ----
  // A crash inside a screen shows a recoverable panel instead of blanking the
  // whole app — the sidebar/nav stay usable, and changing route remounts it.
  class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null }; }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) { console.error("Screen error:", error, info); }
    render() {
      if (!this.state.error) return this.props.children;
      const msg = (this.state.error && this.state.error.message) || String(this.state.error);
      return React.createElement("div", { className: "card card--pad stack-v", style: { gap: 14, maxWidth: 560, margin: "48px auto", alignItems: "center", textAlign: "center" } },
        React.createElement("div", { className: "stat__icon", style: { background: "var(--md-error-container)", width: 56, height: 56, borderRadius: 16 } },
          React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-on-error-container)", fontSize: 30 } }, "error")),
        React.createElement("h2", { className: "section-title", style: { margin: 0 } }, "Something went wrong"),
        React.createElement("p", { className: "muted", style: { margin: 0, maxWidth: 440, lineHeight: 1.5 } }, "This page hit an unexpected error. Try again, or use the menu to open another page."),
        React.createElement("p", { className: "mono", style: { fontSize: 12, color: "var(--md-error)", maxWidth: 520, wordBreak: "break-word", margin: 0 } }, msg),
        React.createElement("div", { className: "row", style: { gap: 10 } },
          React.createElement("button", { className: "btn btn--tonal", onClick: () => this.setState({ error: null }) }, "Try again"),
          React.createElement("button", { className: "btn btn--filled", onClick: () => window.location.reload() },
            React.createElement("span", { className: "material-symbols-rounded" }, "refresh"), "Reload")));
    }
  }

  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "nav": "sidebar",
    "dashLayout": "balanced",
    "fundViz": "donut",
    "requestFlow": "single",
    "dark": false
  }/*EDITMODE-END*/;

  // identities available in the "View as" switcher (demonstrates scopes)
  function identities() {
    // In API mode /people is RBAC-scoped, so source the demo switcher from the
    // full dev-users roster captured by the bootstrap.
    const src = USE_API ? (window.__WIDO_ROSTER || []) : D.people;
    const byId = (id) => src.find((p) => p.id === id);
    return [
      byId("u4"),                              // employee
      byId("u5"),                              // employee with a daily nursing grant
      byId("u1"),                              // team leader (Art + 3D)
      src.find((p) => p.roleId === "hr"),      // HR
      byId("u_ceo"),                           // BOD
    ].filter(Boolean);
  }

  function getMonthFor(person, settings) {
    const raw = (person.id === D.ME.id)
      ? D.meMonth.records
      : (D.companyStats.find((x) => x.person.id === person.id) || {}).records || D.meMonth.records;
    const m = D.helpers.computeMonth(raw, person.id, settings);
    const src = (person.id === D.ME.id) ? D.meMonth : (D.companyStats.find((x) => x.person.id === person.id) || {});
    const taken = src.leaveDays != null ? src.leaveDays : D.meMonth.leaveDays;
    const offsetDays = src.offsetDays || 0;         // annual leave spent on leave-offsets
    // Carry the LIVE month so monthMetrics counts the right calendar (holidays,
    // comp days, weekends). computeMonth doesn't stamp year/month, and without
    // them monthMetrics falls back to the module-scope mock CUR_MONTH (June) —
    // silently computing the wrong month's standard days on the dashboard.
    return { ...m, year: D.CUR_YEAR, month: D.CUR_MONTH, leaveDays: taken, offsetMin: src.offsetMin || 0, offsetDays,
      overtimeMin: src.overtimeMin || 0,            // weekend OT (backend) -> Hours summary
      hasDaily: D.helpers.dailyActive(person.id, settings),
      // Offset days lower the balance like a leave day (kept out of leaveDays).
      leave: D.helpers.computeLeave(person.id, settings, (taken || 0) + offsetDays, person) };
  }

  function roleFor(person) { return D.ROLES.find((r) => r.id === person.roleId) || D.ROLES[0]; }

  // nav definition with permission gates
  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: "dashboard", group: "Me" },
    { id: "timesheet", label: "My timesheet", short: "Timesheet", icon: "calendar_month", perm: "attendance.view.own", group: "Me" },
    { id: "requests", label: "Leave Request", short: "Leave", icon: "playlist_add_check", perm: "request.create", group: "Me" },
    { id: "approvals", label: "Approvals", icon: "fact_check", perm: ["request.approve.team", "request.approve.all", "request.view.all"], group: "Manage" },
    { id: "summary", label: "Team summary", short: "Summary", icon: "insights", perm: ["attendance.view.team", "attendance.view.all"], group: "Manage" },
    { id: "records", label: "Late records", short: "Records", icon: "groups", perm: ["attendance.view.team", "attendance.view.all"], group: "Manage" },
    { id: "override", label: "Manual override", short: "Override", icon: "edit_calendar", perm: "attendance.override", group: "Manage" },
    { id: "missing", label: "Missing clock records", short: "Missing", icon: "pending_actions", perm: "attendance.override", group: "Manage" },
    { id: "activations", label: "Employees", icon: "badge", perm: "account.activate", group: "Admin" },
    { id: "teams", label: "Team assignment", short: "Teams", icon: "diversity_3", perm: "user.manage", group: "Admin" },
    { id: "devices", label: "Device Mapping", short: "Devices", icon: "face", perm: ["device.manage", "attendance.settings"], group: "Admin" },
    { id: "settings", label: "Attendance settings", short: "Settings", icon: "tune", perm: "attendance.settings", group: "Admin" },
    { id: "holidays", label: "Working Calendar", short: "Calendar", icon: "celebration", perm: "attendance.settings", group: "Admin" },
    { id: "access", label: "Access control", short: "Access", icon: "admin_panel_settings", perm: "role.manage", group: "Admin" },
    { id: "hoursreport", label: "Hours report", short: "Report", icon: "table_view", perm: "payroll.report", group: "Admin" },
    { id: "system", label: "System settings", short: "System", icon: "settings", perm: "system.settings", group: "Admin" },
    { id: "calendar", label: "Everyone Calendar", short: "Calendar", icon: "calendar_today", group: "Company" },
    { id: "meetings", label: "Meeting rooms", short: "Rooms", icon: "meeting_room", group: "Company" },
    { id: "guide", label: "Guide", short: "Guide", icon: "help", group: "Company" },
    { id: "policies", label: "Policies", icon: "gavel", perm: "policy.view", group: "Company" },
  ];

  function hasPerm(role, perm) {
    if (!perm) return true;
    const list = Array.isArray(perm) ? perm : [perm];
    return list.some((p) => role.perms.includes(p));
  }

  function Toast({ msg }) {
    if (!msg) return null;
    return React.createElement("div", { style: { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 200, background: "var(--md-inverse-surface)", color: "var(--md-inverse-on-surface)", padding: "14px 22px", borderRadius: "var(--shape-sm)", boxShadow: "var(--elevation-3)", font: "500 14px/1 var(--font-sans)", display: "flex", gap: 10, alignItems: "center", animation: "pop .2s var(--ease-emphasized)" } },
      React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 18, color: "var(--md-inverse-primary)" } }, "check_circle"), msg);
  }

  function Brandmark() {
    return React.createElement("div", { className: "row", style: { gap: 10 } },
      React.createElement("div", { className: "brandmark" }, React.createElement("span", { className: "material-symbols-rounded" }, "sports_esports")),
      React.createElement("div", { className: "stack-v", style: { gap: 0 } },
        React.createElement("span", { style: { font: "900 18px/1 var(--font-brand)", letterSpacing: "-.5px" } }, "Wido Ops"),
        React.createElement("span", { className: "muted", style: { fontSize: 11 } }, "HR & attendance")),
    );
  }

  function NavList({ items, route, onNav, badges }) {
    const groups = [...new Set(items.map((i) => i.group))];
    return React.createElement("div", { className: "sidebar__nav" },
      groups.map((g) => React.createElement("div", { key: g },
        React.createElement("div", { className: "navgroup-label" }, g),
        items.filter((i) => i.group === g).map((i) => React.createElement("button", {
          key: i.id, className: "navitem" + (route === i.id ? " is-active" : ""), onClick: () => onNav(i.id),
        },
          React.createElement("span", { className: "material-symbols-rounded" }, i.icon),
          React.createElement("span", null, i.label),
          badges[i.id] ? React.createElement("span", { className: "navitem__badge" }, badges[i.id]) : null,
        )),
      )),
    );
  }

  // Enable/disable browser push notifications (Web Push). Hidden on browsers
  // that can't do push; disabled when the user has blocked notifications.
  function PushToggle() {
    const [st, setSt] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
      let live = true;
      (window.WidoPush ? window.WidoPush.status() : Promise.resolve({ supported: false }))
        .then((s) => { if (live) setSt(s); });
      return () => { live = false; };
    }, []);
    if (!st || !st.supported) return null;
    const on = st.subscribed && st.permission === "granted";
    const denied = st.permission === "denied";
    async function toggle() {
      if (busy || denied || !window.WidoPush) return;
      setBusy(true);
      try { await (on ? window.WidoPush.disable() : window.WidoPush.enable()); setSt(await window.WidoPush.status()); }
      finally { setBusy(false); }
    }
    return React.createElement("button", { className: "menu__item", disabled: busy || denied, onClick: toggle, title: denied ? window.t("Notifications are blocked in your browser settings.") : "" },
      React.createElement("span", { className: "material-symbols-rounded muted" }, busy ? "hourglass_top" : (on ? "notifications_active" : "notifications")),
      React.createElement("span", { style: { flex: 1, textAlign: "left" } }, denied ? window.t("Notifications blocked") : on ? window.t("Browser notifications on") : window.t("Enable browser notifications")),
      on && React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-primary)", fontSize: 18 } }, "check"));
  }

  function UserMenu({ person, role, idents, onSwitch }) {
    const [open, setOpen] = useState(false);
    return React.createElement("div", { style: { position: "relative" } },
      React.createElement("button", { className: "userchip", onClick: () => setOpen(!open) },
        React.createElement("div", { className: "avatar avatar--sm", style: { background: AV.color(person.name) } }, AV.initials(person.name)),
        React.createElement("div", { className: "stack-v hide-mobile", style: { gap: 0, alignItems: "flex-start" } },
          React.createElement("span", { style: { font: "500 13px/1.2 var(--font-sans)" } }, person.name.split(" ").slice(-2).join(" ")),
          React.createElement("span", { className: "muted", style: { fontSize: 11 } }, role.name)),
        React.createElement("span", { className: "material-symbols-rounded muted", style: { fontSize: 18 } }, "expand_more"),
      ),
      open && React.createElement(React.Fragment, null,
        React.createElement("div", { style: { position: "fixed", inset: 0, zIndex: 39 }, onClick: () => setOpen(false) }),
        React.createElement("div", { className: "menu" },
          React.createElement("div", { className: "menu__head" },
            React.createElement("div", { className: "avatar", style: { background: AV.color(person.name) } }, AV.initials(person.name)),
            React.createElement("div", { className: "stack-v", style: { gap: 1, minWidth: 0 } },
              React.createElement("strong", { style: { fontSize: 14 } }, person.name),
              React.createElement("span", { className: "muted mono", style: { fontSize: 12 } }, person.email || (person.name.toLowerCase().split(" ").slice(-1)[0] + "@widogame.com")))),
          // "View as" is a demo-only role switcher backed by dev-login. In
          // production (DEV_LOGIN off) the roster is empty, so hide the whole
          // section — header, divider and all.
          idents.length > 0 && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "divider", style: { margin: "4px 0" } }),
            React.createElement("div", { className: "navgroup-label", style: { padding: "8px 12px 4px" } }, "View as (demo)"),
            idents.map((p) => {
              const r = roleFor(p);
              return React.createElement("button", { key: p.id, className: "menu__item", onClick: () => { onSwitch(p); setOpen(false); } },
                React.createElement("div", { className: "avatar avatar--sm", style: { background: AV.color(p.name) } }, AV.initials(p.name)),
                React.createElement("div", { className: "stack-v", style: { gap: 0, flex: 1, minWidth: 0 } },
                  React.createElement("span", { style: { fontSize: 13 } }, p.name.split(" ").slice(-2).join(" ")),
                  React.createElement("span", { className: "muted", style: { fontSize: 11 } }, r.name + (p.leads && p.leads.length ? " · " + p.leads.length + " teams" : ""))),
                person.id === p.id && React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-primary)", fontSize: 18 } }, "check"));
            })),
          React.createElement("div", { className: "divider", style: { margin: "4px 0" } }),
          React.createElement(PushToggle),
          React.createElement("button", { className: "menu__item", onClick: async () => { if (window.WidoPush) { try { await window.WidoPush.disable(); } catch (e) {} } if (window.WIDO_USE_API && window.WidoApi) { window.WidoApi.logout().finally(() => location.reload()); } else { location.reload(); } } },
            React.createElement("span", { className: "material-symbols-rounded muted" }, "logout"), "Sign out"),
        ),
      ),
    );
  }

  function App() {
    const [t, setTweak] = window.useTweaks(TWEAK_DEFAULTS);
    const [authed, setAuthed] = useState(null);
    const [route, setRoute] = useState(() => routeFromPath(window.location.pathname).route);
    const [toast, setToast] = useState(null);
    // Device data (devices / deviceEnrollments / faceMap) is backend-owned — it
    // comes from /api/devices via the bootstrap, never from persisted settings.
    // Keeping it out of localStorage avoids a stale cached roster (e.g. the mock
    // "dev1" terminals) overriding the live one on reload.
    const DEVICE_KEYS = ["devices", "deviceEnrollments", "faceMap"];
    const [settings, setSettingsState] = useState(() => {
      try {
        const s = JSON.parse(localStorage.getItem("wido.settings"));
        if (s && s.defaultMinutes != null) {
          const merged = Object.assign({}, JSON.parse(JSON.stringify(D.DEFAULT_SETTINGS)), s);
          DEVICE_KEYS.forEach((k) => { merged[k] = D.DEFAULT_SETTINGS[k]; });
          return merged;
        }
      } catch (e) {}
      return JSON.parse(JSON.stringify(D.DEFAULT_SETTINGS));
    });
    function setSettings(next) {
      setSettingsState(next);
      try {
        const persist = Object.assign({}, next);
        DEVICE_KEYS.forEach((k) => { delete persist[k]; });
        localStorage.setItem("wido.settings", JSON.stringify(persist));
      } catch (e) {}
      // Persist to the backend in API mode (covers defaults, per-person overrides,
      // daily grants, work schedules and the leave backfill — all flow through here).
      if (USE_API && window.WidoApi) window.WidoApi.saveSettings(next).catch(() => showToast("Couldn't save settings"));
    }
    // First-time setup wizard (handoff-3). MOCK/DEMO ONLY: it sets a local founder
    // + initial settings. In API mode, onboarding is backend/SSO-driven, so setup
    // counts as already done and we fall straight through to the real AuthFlow —
    // the wizard never bypasses Google SSO or leaves you without a backend session.
    const [setupDone, setSetupDone] = useState(() => {
      if (USE_API) return true;
      try { return localStorage.getItem("wido.setup") === "done"; } catch (e) { return false; }
    });
    function completeSetup(founder, initSettings) {
      setSettings(initSettings);
      try { localStorage.setItem("wido.setup", "done"); } catch (e) {}
      window.WidoI18n.loadForUser(founder.id); setLangState(window.WidoI18n.lang);
      setSetupDone(true); setAuthed(founder); setRoute("dashboard");
    }
    // API-mode setup: persist the initial settings to the backend (owner-only),
    // which also marks the deployment configured + activates the owner as BOD.
    function completeSetupApi(_founder, initSettings) {
      const dev = (initSettings.devices && initSettings.devices[0]) || null;
      // Load live data and drop the user into the app. Shared by the success and
      // "already configured" paths so we never strand the owner on the wizard.
      const enterApp = () => window.WidoBootstrap.loadAndApply()
        .then((me) => { setSettingsState(window.WidoData.DEFAULT_SETTINGS); setAuthed(me); setRoute("dashboard"); window.scrollTo({ top: 0 }); });
      window.WidoApi.completeSetup({
        defaultMinutes: initSettings.defaultMinutes,
        defaultPerms: initSettings.defaultPerms,
        defaultAnnual: initSettings.defaultAnnual,
        defaultShift: initSettings.defaultShift,
        device: dev ? { identifier: dev.id, name: dev.name, location: dev.location } : null,
      })
        // 409 already_configured: setup completed on an earlier attempt (e.g. the
        // first press succeeded server-side but the follow-up load failed, or a
        // double-click). It's effectively done — proceed into the app rather than
        // showing a misleading "Setup failed" and trapping the owner on the wizard.
        .catch((e) => { if (e && e.status === 409) return; throw e; })
        .then(enterApp)
        .catch((e) => showToast("Setup failed: " + ((e && e.message) || e)));
    }
    const [notifs, setNotifs] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);
    const [showMore, setShowMore] = useState(false); // mobile "More" bottom-nav sheet
    const [syncing, setSyncing] = useState(false);   // background (deferred) data load in flight
    // i18n: lang state drives a re-render so the patched React.createElement
    // re-runs every string child through t() with the new language.
    const [lang, setLangState] = useState(window.WidoI18n.lang);
    function toggleLang() {
      const next = lang === "en" ? "vi" : "en";
      window.WidoI18n.setLang(next, authed && authed.id);
      setLangState(next);
      // Persist to the backend (Person.lang) so it follows the user across devices.
      if (USE_API && window.WidoApi && authed) window.WidoApi.setLang(next).catch(() => {});
    }
    // Apply a user's saved language on login/switch — from the backend (Person.lang)
    // in API mode, else from localStorage.
    function applyUserLang(p) {
      if (USE_API && p && p.lang) window.WidoI18n.setLang(p.lang, p.id);
      else window.WidoI18n.loadForUser(p && p.id);
      setLangState(window.WidoI18n.lang);
    }
    const [detailPerson, setDetailPerson] = useState(null);
    const [dataVersion, setDataVersion] = useState(0);
    // Live count of open clock fix-ups, reported by MissingClockScreen after each
    // of its loads. null = nothing reported yet -> fall back to D.missingCount
    // from the deferred load. Cleared on every data refresh so a fresh
    // D.missingCount wins over a count reported before it.
    const [missingCount, setMissingCount] = useState(null);
    useEffect(() => { setMissingCount(null); }, [dataVersion]);
    // After a write, re-pull live data (API mode) and remount the screen so lists refresh.
    async function refreshData() {
      if (USE_API && window.WidoBootstrap) { try { await window.WidoBootstrap.loadAndApply(); } catch (e) {} }
      setDataVersion((v) => v + 1);
    }
    // Lightweight refresh for roster edits (team assignment): re-fetch only the
    // people list, skipping the heavy company-wide attendance rollup
    // (records?scope=all) that loadAndApply runs — a team change doesn't affect
    // anyone's computed attendance, so recomputing it just froze the UI.
    async function refreshRoster() {
      if (USE_API && window.WidoBootstrap && window.WidoBootstrap.refreshRoster) {
        try { await window.WidoBootstrap.refreshRoster(); } catch (e) {}
      }
      setDataVersion((v) => v + 1);
    }
    // Post-login: the critical data is already in; stream the heavy company-wide
    // data (roster, everyone's month, approvals, notifications, devices, policies)
    // in the background, then refresh the screens/badges/notifications that use it.
    function startDeferredLoad(p) {
      if (!USE_API || !window.WidoBootstrap || !window.WidoBootstrap.loadDeferred) return;
      setSyncing(true);
      window.WidoBootstrap.loadDeferred()
        .then(() => {
          setSettingsState(window.WidoData.DEFAULT_SETTINGS); // fold in live device mapping
          setNotifs(window.notificationsFor(p));
          setDataVersion((v) => v + 1);                       // remount screens, recompute badges
        })
        .catch(() => {})
        .finally(() => setSyncing(false));
    }
    function viewEmployee(p) {
      setDetailPerson(p); setRoute("employee"); window.scrollTo({ top: 0 });
      window.history.pushState({ route: "employee", id: p.id }, "", "/employee/" + encodeURIComponent(p.id));
    }
    // Browser back/forward: re-derive the page from the URL.
    useEffect(() => {
      function onPop() {
        const parsed = routeFromPath(window.location.pathname);
        if (parsed.route === "employee" && parsed.id) {
          const p = D.helpers.byId(parsed.id) || ((D.companyStats.find((s) => s.person.id === parsed.id) || {}).person);
          if (p) { setDetailPerson(p); setRoute("employee"); window.scrollTo({ top: 0 }); return; }
        }
        setDetailPerson(null); setRoute(parsed.route); window.scrollTo({ top: 0 });
      }
      window.addEventListener("popstate", onPop);
      return () => window.removeEventListener("popstate", onPop);
    }, []);
    useEffect(() => { if (authed) { setNotifs(window.notificationsFor(authed)); setShowNotifs(false); } }, [authed]);
    function markAllRead() {
      setNotifs((ns) => ns.map((n) => ({ ...n, unread: false })));
      if (USE_API && window.WidoApi) window.WidoApi.markAllRead().catch(() => {});
    }
    // Open a notification: mark it read (one-way; there's no per-item backend
    // endpoint, so use "Mark all read" to persist), close the panel, and route
    // to the notification's deep-link if it has one.
    function openNotif(item) {
      setNotifs((ns) => ns.map((n) => n.id === item.id ? { ...n, unread: false } : n));
      setShowNotifs(false);
      if (item && item.link) { const r = routeFromPath(item.link).route; if (r) nav(r); }
    }

    useEffect(() => { document.documentElement.dataset.theme = t.dark ? "dark" : "light"; }, [t.dark]);

    // Live Approvals queue: poll the pending list every 5s (only while the tab is
    // visible) and, ONLY when the set of pending requests actually changes, pull
    // fresh data + remount — so a request someone else just filed (or decided)
    // shows up without a manual reload, and the steady state causes no UI churn.
    // Polling, not SSE/WebSocket: the backend runs sync gunicorn workers, which a
    // long-lived stream per open tab would tie up.
    const pollSigRef = React.useRef(null);
    useEffect(() => {
      if (!USE_API || !authed || !window.WidoApi || !window.WidoApi.approvals) return;
      let alive = true;
      pollSigRef.current = null;  // re-seed for this session
      async function tick() {
        if (document.hidden || !alive) return;
        try {
          const list = await window.WidoApi.approvals("all");
          const sig = (Array.isArray(list) ? list : []).map((r) => r.id).sort().join(",");
          if (pollSigRef.current === null) { pollSigRef.current = sig; return; }  // first read seeds, no refresh
          if (sig !== pollSigRef.current) { pollSigRef.current = sig; if (alive) refreshData(); }
        } catch (e) { /* transient network/auth error: ignore, retry next tick */ }
      }
      const id = setInterval(tick, 5000);
      const onVis = () => { if (!document.hidden) tick(); };  // catch up at once on tab refocus
      document.addEventListener("visibilitychange", onVis);
      return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    }, [authed]);

    function showToast(m) { setToast(m); clearTimeout(window.__tt); window.__tt = setTimeout(() => setToast(null), 2600); }

    // First-time setup runs before login — mock/demo only (skipped in API mode).
    if (!USE_API && !setupDone) return React.createElement(React.Fragment, null,
      React.createElement(window.SetupFlow, { baseSettings: settings, onComplete: completeSetup }),
      React.createElement(TweaksHost, { t, setTweak }),
    );

    if (!authed) return React.createElement(React.Fragment, null,
      React.createElement(window.AuthFlow, { onAuthed: (p) => { if (USE_API) { setSettingsState(window.WidoData.DEFAULT_SETTINGS); startDeferredLoad(p); } applyUserLang(p); setAuthed(p); setRoute(routeFromPath(window.location.pathname).route); window.scrollTo({ top: 0 }); } }),
      React.createElement(TweaksHost, { t, setTweak }),
    );

    // API mode: an unconfigured deployment shows the setup wizard to the signed-in
    // OWNER (post-auth). Prefer the authoritative GET /setup/status ({configured,
    // isOwner}) so we never re-show the wizard for a deployment the server already
    // considers configured (a stale /settings snapshot would otherwise trap the
    // owner on the wizard -> 409 on complete). Fall back to the settings flag +
    // the build-time owner email only when status is unavailable.
    const setupStatus = window.WidoData.SETUP_STATUS;
    const needsSetup = setupStatus
      ? (setupStatus.configured === false && setupStatus.isOwner === true)
      : (window.WidoSetup && authed.email === window.WidoSetup.EMAIL && settings && settings.configured === false);
    if (USE_API && needsSetup)
      return React.createElement(React.Fragment, null,
        React.createElement(window.SetupFlow, { baseSettings: settings, apiMode: true, onComplete: completeSetupApi }),
        React.createElement(TweaksHost, { t, setTweak }),
      );

    const role = roleFor(authed);
    const month = getMonthFor(authed, settings);
    const visibleNav = NAV.filter((i) => hasPerm(role, i.perm));
    // ensure route is permitted ('employee' is a drill-in detail view for managers)
    const canViewOthers = hasPerm(role, "attendance.view.team") || hasPerm(role, "attendance.view.all");
    const activeRoute = (route === "employee" && detailPerson && canViewOthers) ? "employee"
      : visibleNav.some((i) => i.id === route) ? route : "dashboard";

    const recordsScope = hasPerm(role, "attendance.view.all") ? "all" : "team";
    // Company-wide view (HR's request.view.all or BOD's approve.all) -> see all; else team.
    const approvalsScope = (hasPerm(role, "request.view.all") || hasPerm(role, "request.approve.all")) ? "all" : "team";

    // badges — count only requests the viewer can ACT on (HR sees every request
    // but acts on offsets alone), so the badge mirrors the actionable queue.
    const pendingApprovals = D.requests.filter((r) => r.status === "pending" && D.helpers.canActOnRequest(authed, r)).length;
    const badges = {
      approvals: pendingApprovals || null,
      activations: hasPerm(role, "account.activate") ? D.pendingAccounts.length : null,
      // Open clock fix-ups this month. Seeded by the deferred load, then kept
      // exact by the screen itself (`onCount`) as HR resolves cases — resolving
      // one must not leave the badge counting it, and a full refreshData would
      // remount the screen and throw away the month/filter they're working in.
      missing: hasPerm(role, "attendance.override")
        ? ((missingCount != null ? missingCount : (D.missingCount || 0)) || null)
        : null,
    };

    function nav(id) {
      setRoute(id); window.scrollTo({ top: 0 });
      const path = pathForRoute(id, null);
      if (window.location.pathname !== path) window.history.pushState({ route: id }, "", path);
    }

    let screen, title, sub;
    switch (activeRoute) {
      case "dashboard": title = "Hi, " + authed.name.split(" ").slice(-1)[0]; sub = D.MONTH_LABEL + " · " + role.name;
        screen = React.createElement(window.Dashboard, { t, me: authed, month, onNav: nav, settings }); break;
      case "timesheet": title = "Timesheet"; screen = React.createElement(window.TimesheetScreen, { person: authed, settings }); break;
      case "requests": title = "Leave Request"; screen = React.createElement(window.RequestsScreen, { t, settings, onToast: showToast, onRefresh: refreshData, canAdmin: hasPerm(role, "request.view.all") || hasPerm(role, "request.approve.all"), viewer: authed }); break;
      case "approvals": title = "Approvals"; screen = React.createElement(window.ApprovalsScreen, { scope: approvalsScope, me: authed, onToast: showToast, onRefresh: refreshData }); break;
      case "summary": title = "Team summary"; screen = React.createElement(window.TeamSummaryScreen, { scope: recordsScope, me: authed, settings, onView: viewEmployee }); break;
      case "records": title = "Late records"; screen = React.createElement(window.RecordsScreen, { scope: recordsScope, me: authed, settings, onView: viewEmployee }); break;
      case "override": title = "Manual override"; screen = React.createElement(window.OverrideScreen, { settings, onToast: showToast, onRefresh: refreshData }); break;
      case "missing": title = "Missing clock records"; screen = React.createElement(window.MissingClockScreen, { settings, onToast: showToast, onCount: setMissingCount }); break;
      case "activations": title = "Employees"; screen = React.createElement(window.ActivationsScreen, { settings, setSettings, onToast: showToast, onRefresh: refreshData }); break;
      case "teams": title = "Team assignment"; screen = React.createElement(window.TeamAssignScreen, { onToast: showToast, onRefresh: refreshRoster }); break;
      case "devices": title = "Device Mapping"; screen = React.createElement(window.DeviceMappingScreen, { settings, setSettings, onToast: showToast }); break;
      case "settings": title = "Attendance settings"; screen = React.createElement(window.SettingsScreen, { settings, setSettings, onToast: showToast }); break;
      case "holidays": title = "Working Calendar"; screen = React.createElement(window.HolidaysScreen, { settings, setSettings, onToast: showToast }); break;
      case "access": title = "Access control"; screen = React.createElement(window.AdminScreen, { onToast: showToast, onRefresh: refreshData }); break;
      case "hoursreport": title = "Hours report"; screen = React.createElement(window.HoursReportScreen, { settings, onToast: showToast }); break;
      case "system": title = "System settings"; screen = React.createElement(window.SystemSettingsScreen, { settings, onSaved: (system) => setSettingsState(Object.assign({}, settings, { system })), onToast: showToast }); break;
      case "calendar": title = "Everyone Calendar"; screen = React.createElement(window.CalendarScreen, { me: authed, settings }); break;
      case "meetings": title = "Meeting rooms"; screen = React.createElement(window.MeetingsScreen, { me: authed, onToast: showToast, canReconcile: hasPerm(role, "meetings.calendar.view.all") }); break;
      case "guide": title = "Guide"; screen = React.createElement(window.GuideScreen, null); break;
      case "policies": title = "Policies"; screen = React.createElement(window.PolicyScreen, null); break;
      case "employee": title = "Employee detail"; sub = detailPerson ? (D.helpers.roleName(detailPerson.roleId) + " · " + D.helpers.teamName(detailPerson.team)) : "";
        screen = React.createElement(window.EmployeeDetailScreen, { person: detailPerson, settings, t, viewer: authed, onNav: nav, onBack: () => {
          // Pop the /employee/:id entry so the browser back button and this button agree.
          if (window.history.state && window.history.state.route === "employee") { window.history.back(); }
          else { setDetailPerson(null); nav(hasPerm(role, "attendance.view.all") ? "records" : "summary"); }
        } }); break;
      default: screen = null;
    }

    const useSidebar = t.nav === "sidebar";

    return React.createElement("div", { className: "app" },
      // Thin top progress bar while the deferred (company-wide) data streams in
      // after login — signals "still loading" without blocking the dashboard.
      syncing && React.createElement("div", { style: { position: "fixed", top: 0, left: 0, right: 0, height: 3, overflow: "hidden", zIndex: 100, pointerEvents: "none" } },
        React.createElement("div", { style: { width: "40%", height: "100%", background: "var(--md-primary)", borderRadius: 99, animation: "bootbar 1.1s ease-in-out infinite" } })),
      useSidebar && React.createElement("aside", { className: "sidebar hide-mobile" },
        React.createElement("div", { className: "sidebar__brand" }, React.createElement(Brandmark, null)),
        React.createElement(NavList, { items: visibleNav, route: activeRoute, onNav: nav, badges }),
        React.createElement("div", { className: "sidebar__foot" },
          React.createElement("div", { className: "card", style: { padding: 12, background: "var(--md-surface-container)", border: "none" } },
            React.createElement("div", { className: "row", style: { gap: 8 } },
              React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-primary)", fontSize: 18 } }, "verified" ),
              React.createElement("span", { style: { font: "500 12px/1.3 var(--font-sans)" } }, "Synced with Google Workspace")))),
      ),
      React.createElement("div", { className: "app__main" },
        React.createElement("header", { className: "appbar" },
          !useSidebar && React.createElement("div", { className: "hide-mobile", style: { marginRight: 8 } }, React.createElement(Brandmark, null)),
          useSidebar && React.createElement("div", { className: "stack-v", style: { gap: 0 } },
            React.createElement("span", { className: "appbar__title" }, title),
            sub && React.createElement("span", { className: "appbar__sub" }, sub)),
          !useSidebar && React.createElement("nav", { className: "topnav hide-mobile" },
            visibleNav.map((i) => React.createElement("button", { key: i.id, className: "navitem" + (activeRoute === i.id ? " is-active" : ""), onClick: () => nav(i.id) },
              React.createElement("span", { className: "material-symbols-rounded" }, i.icon),
              React.createElement("span", null, i.label),
              badges[i.id] ? React.createElement("span", { className: "navitem__badge" }, badges[i.id]) : null))),
          React.createElement("div", { className: "appbar__spacer" }),
          React.createElement("button", { className: "icon-btn", title: "Toggle theme", onClick: () => setTweak("dark", !t.dark) },
            React.createElement("span", { className: "material-symbols-rounded" }, t.dark ? "light_mode" : "dark_mode")),
          React.createElement("button", { className: "icon-btn", title: "Language", onClick: toggleLang, style: { width: "auto", padding: "0 12px", gap: 6, display: "flex", alignItems: "center" } },
            React.createElement("span", { className: "material-symbols-rounded" }, "language"),
            React.createElement("span", { style: { font: "600 13px/1 var(--font-mono)" } }, lang === "en" ? "EN" : "VI")),
          React.createElement("div", { style: { position: "relative" }, className: "hide-mobile" },
            React.createElement("button", { className: "icon-btn", title: "Notifications", onClick: () => setShowNotifs((v) => !v) },
              React.createElement("span", { className: "material-symbols-rounded" }, "notifications"),
              notifs.filter((n) => n.unread).length > 0 && React.createElement("span", { style: { position: "absolute", top: 6, right: 6, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: "var(--md-tertiary)", color: "var(--md-on-tertiary)", font: "600 10px/16px var(--font-mono)", textAlign: "center" } }, notifs.filter((n) => n.unread).length)),
            showNotifs && React.createElement(window.NotificationsPanel, { items: notifs, onClose: () => setShowNotifs(false), onMarkAll: markAllRead, onOpen: openNotif })),
          React.createElement(UserMenu, { person: authed, role, idents: identities(),
            onSwitch: USE_API
              ? async (p) => { try { await window.WidoBootstrap.switchTo(p.id); setSettingsState(window.WidoData.DEFAULT_SETTINGS); const me = window.WidoData.ME; applyUserLang(me); setAuthed(me); showToast("Viewing as " + roleFor(me).name); } catch (e) { showToast("Switch failed"); } }
              : (p) => { applyUserLang(p); setAuthed(p); showToast("Viewing as " + roleFor(p).name); } }),
        ),
        React.createElement("main", { className: "content" },
          React.createElement(ErrorBoundary, { key: activeRoute + ":" + dataVersion }, screen)),
      ),
      // mobile bottom nav (first 4 visible items + more)
      React.createElement("nav", { className: "bottomnav" },
        React.createElement("div", { className: "bottomnav__inner" },
          // first 4 sections inline; the rest live behind a "More" sheet
          visibleNav.slice(0, 4).map((i) => React.createElement("button", { key: i.id, className: "bottomnav__item" + (activeRoute === i.id ? " is-active" : ""), onClick: () => nav(i.id) },
            React.createElement("span", { className: "bottomnav__pill" }, React.createElement("span", { className: "material-symbols-rounded" }, i.icon)),
            React.createElement("span", null, i.short || i.label))),
          React.createElement("button", { className: "bottomnav__item" + (visibleNav.slice(4).some((i) => i.id === activeRoute) ? " is-active" : ""), onClick: () => setShowMore(true) },
            React.createElement("span", { className: "bottomnav__pill" }, React.createElement("span", { className: "material-symbols-rounded" }, "menu")),
            React.createElement("span", null, "More")))),
      showMore && React.createElement("div", { className: "scrim-bg", style: { alignItems: "flex-end" }, onClick: () => setShowMore(false) },
        React.createElement("div", { className: "sheet", style: { maxWidth: 560, borderRadius: "var(--shape-xl) var(--shape-xl) 0 0" }, onClick: (e) => e.stopPropagation() },
          React.createElement("div", { className: "sheet__head", style: { paddingBottom: 8 } },
            React.createElement("h3", { className: "section-title", style: { fontSize: 20 } }, "All sections"),
            React.createElement("button", { className: "icon-btn", onClick: () => setShowMore(false) }, React.createElement("span", { className: "material-symbols-rounded" }, "close"))),
          React.createElement("div", { className: "sheet__body", style: { paddingTop: 4 } },
            React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 } },
              visibleNav.map((i) => React.createElement("button", {
                key: i.id, className: "gaccount", style: { borderColor: activeRoute === i.id ? "var(--md-primary)" : "var(--md-outline-variant)", background: activeRoute === i.id ? "color-mix(in srgb, var(--md-primary) 8%, transparent)" : "transparent" },
                onClick: () => { nav(i.id); setShowMore(false); } },
                React.createElement("span", { className: "material-symbols-rounded", style: { color: activeRoute === i.id ? "var(--md-primary)" : "var(--md-on-surface-variant)" } }, i.icon),
                React.createElement("span", { style: { font: "500 14px/1.2 var(--font-sans)" } }, i.label),
                badges[i.id] ? React.createElement("span", { className: "navitem__badge", style: { position: "static", marginLeft: "auto" } }, badges[i.id]) : null)))))),
      React.createElement(Toast, { msg: toast }),
      React.createElement(TweaksHost, { t, setTweak }),
    );
  }

  function TweaksHost({ t, setTweak }) {
    const { TweaksPanel, TweakSection, TweakRadio, TweakToggle } = window;
    return React.createElement(TweaksPanel, null,
      React.createElement(TweakSection, { label: "Navigation" }),
      React.createElement(TweakRadio, { label: "Layout", value: t.nav, options: ["sidebar", "topbar"], onChange: (v) => setTweak("nav", v) }),
      React.createElement(TweakSection, { label: "Dashboard" }),
      React.createElement(TweakRadio, { label: "Arrangement", value: t.dashLayout, options: ["balanced", "charts", "cards"], onChange: (v) => setTweak("dashLayout", v) }),
      React.createElement(TweakRadio, { label: "Fund visual", value: t.fundViz, options: ["donut", "linear", "segmented"], onChange: (v) => setTweak("fundViz", v) }),
      React.createElement(TweakSection, { label: "Requests" }),
      React.createElement(TweakRadio, { label: "Submit flow", value: t.requestFlow, options: ["single", "wizard", "quick"], onChange: (v) => setTweak("requestFlow", v) }),
      React.createElement(TweakSection, { label: "Theme" }),
      React.createElement(TweakToggle, { label: "Dark mode", value: t.dark, onChange: (v) => setTweak("dark", v) }),
    );
  }

  window.WidoApp = App;
})();
